<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import Fuse from 'fuse.js'
import Provider from '@/api/Provider'

const router = useRouter()

// Search functionality
const searchQuery = ref('')
const providers = ref([])
const searchResults = ref([])
const showDropdown = ref(false)
const selectedIndex = ref(-1)
const searchInputRef = ref(null)
const dropdownRef = ref(null)
const isLoading = ref(false)
const debounceTimer = ref(null)

// Load providers from API
const loadProviders = async () => {
  isLoading.value = true
  try {
    const result = await Provider.listProviders()
    providers.value = result
  } catch (error) {
    console.error('Failed to load providers:', error)
  } finally {
    isLoading.value = false
  }
}

// Configure Fuse.js for fuzzy search
const fuse = computed(() => {
  if (!providers.value.length) return null

  return new Fuse(providers.value, {
    keys: [
      { name: 'display_name', weight: 0.7 },
      { name: 'name', weight: 0.2 },
      { name: 'description', weight: 0.1 }
    ],
    threshold: 0.4,
    includeScore: true,
    minMatchCharLength: 2
  })
})

// Perform fuzzy search
const performSearch = () => {
  if (!searchQuery.value.trim() || !fuse.value) {
    searchResults.value = []
    showDropdown.value = false
    selectedIndex.value = -1
    return
  }

  const results = fuse.value.search(searchQuery.value)
  searchResults.value = results.slice(0, 5).map(result => result.item)
  showDropdown.value = true
  selectedIndex.value = -1
}

// Watch search query with debounce
watch(searchQuery, () => {
  if (debounceTimer.value) {
    clearTimeout(debounceTimer.value)
  }

  debounceTimer.value = setTimeout(() => {
    performSearch()
  }, 300)
})

// Keyboard navigation
const handleKeydown = (event) => {
  if (!showDropdown.value || searchResults.value.length === 0) return

  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      selectedIndex.value = (selectedIndex.value + 1) % searchResults.value.length
      break

    case 'ArrowUp':
      event.preventDefault()
      selectedIndex.value = selectedIndex.value <= 0
        ? searchResults.value.length - 1
        : selectedIndex.value - 1
      break

    case 'Enter':
      event.preventDefault()
      if (selectedIndex.value >= 0 && selectedIndex.value < searchResults.value.length) {
        navigateToProvider(searchResults.value[selectedIndex.value])
      }
      break

    case 'Escape':
      event.preventDefault()
      closeDropdown()
      break
  }
}

// Navigate to provider profile
const navigateToProvider = (provider) => {
  router.push({
    name: 'ProviderProfile',
    params: { name: provider.name }
  })
  closeDropdown()
  searchQuery.value = ''
}

// Close dropdown
const closeDropdown = () => {
  showDropdown.value = false
  selectedIndex.value = -1
}

// Click outside handler
const handleClickOutside = (event) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target) &&
      searchInputRef.value && !searchInputRef.value.contains(event.target)) {
    closeDropdown()
  }
}

// Ctrl+K handler to focus search
const handleGlobalKeydown = (event) => {
  if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
    event.preventDefault()
    searchInputRef.value?.focus()
  }
}

onMounted(() => {
  // Load providers for search
  loadProviders()

  // Add click outside listener
  document.addEventListener('click', handleClickOutside)

  // Add Ctrl+K listener
  document.addEventListener('keydown', handleGlobalKeydown)
})

onUnmounted(() => {
  // Cleanup click outside listener
  document.removeEventListener('click', handleClickOutside)

  // Cleanup Ctrl+K listener
  document.removeEventListener('keydown', handleGlobalKeydown)

  // Cleanup debounce timer
  if (debounceTimer.value) {
    clearTimeout(debounceTimer.value)
  }
})
</script>

<template>
  <div class="search-container" ref="dropdownRef">
    <input
      ref="searchInputRef"
      v-model="searchQuery"
      type="text"
      placeholder="Search for providers"
      class="search-input"
      @keydown="handleKeydown"
      @focus="searchQuery && performSearch()"
    />
    <div class="search-hint">
      <span class="search-hint-key">Ctrl</span>
      <span class="search-hint-key">K</span>
    </div>

    <!-- Search Results Dropdown -->
    <div v-if="showDropdown" class="search-dropdown">
      <div
        v-for="(provider, index) in searchResults"
        :key="provider.id"
        class="search-result-item"
        :class="{ 'selected': index === selectedIndex }"
        @click="navigateToProvider(provider)"
        @mouseenter="selectedIndex = index"
      >
        <div class="provider-icon">
          <img
            v-if="provider.thumbnail_url"
            :src="provider.thumbnail_url"
            :alt="provider.display_name || provider.name"
          />
          <div v-else class="provider-icon-fallback">
            {{ (provider.display_name || provider.name).charAt(0).toUpperCase() }}
          </div>
        </div>
        <div class="provider-info">
          <div class="provider-name">
            {{ provider.display_name || provider.name }}
          </div>
          <div v-if="provider.description" class="provider-description">
            {{ provider.description.substring(0, 60) }}{{ provider.description.length > 60 ? '...' : '' }}
          </div>
        </div>
      </div>

      <div v-if="searchResults.length === 0 && !isLoading" class="search-no-results">
        No providers found
      </div>

      <div v-if="isLoading" class="search-loading">
        Loading providers...
      </div>
    </div>
  </div>
</template>

<style scoped>
.search-container {
  position: relative;
  display: flex;
  width: 600px;
  flex-shrink: 0;
}

.search-input {
  width: 100%;
  background-color: #171C24;
  border: 1px solid #374151;
  border-radius: 10px;
  color: white;
  padding: 0.75rem 1rem;
  padding-right: 5rem;
  outline: none;
  font-family: "Inter", sans-serif;
  font-size: 0.95rem;
  transition: border-color 0.2s;
}

.search-input::placeholder {
  color: #6B7280;
}

.search-input:focus {
  border-color: #7357D2;
}

.search-hint {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  gap: 0.25rem;
  pointer-events: none;
}

.search-hint-key {
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #7A8696;
  padding: 0.15rem 0.4rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-family: "Inter", sans-serif;
  font-weight: 500;
}

.search-dropdown {
  position: absolute;
  top: calc(100% + 0.5rem);
  left: 0;
  right: 0;
  background-color: #1F2631;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  z-index: 1000;
  max-height: 400px;
  overflow-y: auto;
}

.search-result-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.875rem 1rem;
  cursor: pointer;
  transition: background-color 0.15s ease;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.search-result-item:last-child {
  border-bottom: none;
}

.search-result-item:hover,
.search-result-item.selected {
  background-color: #232A35;
}

.provider-icon {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  background-color: #2A3441;
  display: flex;
  align-items: center;
  justify-content: center;
}

.provider-icon img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.provider-icon-fallback {
  font-size: 1.25rem;
  font-weight: 600;
  color: #7A8696;
}

.provider-info {
  flex: 1;
  min-width: 0;
}

.provider-name {
  font-size: 0.95rem;
  font-weight: 500;
  color: white;
  margin-bottom: 0.25rem;
}

.provider-description {
  font-size: 0.825rem;
  color: #7A8696;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.search-loading {
  padding: 1rem;
  text-align: center;
  color: #7A8696;
  font-size: 0.875rem;
}

.search-no-results {
  padding: 1rem;
  text-align: center;
  color: #7A8696;
  font-size: 0.875rem;
}
</style>
